import React, { useState, useEffect, useRef } from 'react';
import { Tag, X, ChevronDown, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * LabelSelector Component
 * Allows selecting and managing labels for leads/properties
 */
const LabelSelector = ({ 
  entityId, 
  entityType = 'lead', // 'lead' or 'property'
  selectedLabels = [], 
  onLabelsChange,
  editable = true,
  showTitle = true
}) => {
  const { token } = useAuth();
  const [allLabels, setAllLabels] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Load all available labels
  useEffect(() => {
    const loadLabels = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/labels`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAllLabels(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Failed to load labels');
      }
    };
    if (token) loadLabels();
  }, [token]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleLabel = async (labelId) => {
    if (!editable || !entityId) return;

    const newLabels = selectedLabels.includes(labelId)
      ? selectedLabels.filter(id => id !== labelId)
      : [...selectedLabels, labelId];

    try {
      setLoading(true);
      const endpoint = entityType === 'property' 
        ? `${API_BASE}/api/properties/${entityId}/labels`
        : `${API_BASE}/api/leads/${entityId}/labels`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ labels: newLabels })
      });

      if (response.ok) {
        onLabelsChange?.(newLabels);
      }
    } catch (err) {
      console.error('Failed to update labels');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedLabelObjects = () => {
    if (!selectedLabels || selectedLabels.length === 0) return [];
    return selectedLabels.map(id => {
      const found = allLabels.find(l => l.id === id);
      return found || { id, name: 'Unknown Label', color: '#9ca3af' };
    });
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="label-selector">
      {showTitle && (
        <label className="block text-sm font-medium text-gray-700 mb-2">Labels</label>
      )}
      
      {/* Selected Labels Display */}
      <div className="flex flex-wrap gap-2 mb-2">
        {getSelectedLabelObjects().map(label => (
          <span
            key={label.id}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full"
            style={{ 
              backgroundColor: (label.color || '#9ca3af') + '20',
              color: label.color || '#9ca3af'
            }}
            data-testid={`selected-label-${label.id}`}
          >
            {label.name}
            {editable && (
              <button
                onClick={() => handleToggleLabel(label.id)}
                className="hover:opacity-70"
                disabled={loading}
              >
                <X size={12} />
              </button>
            )}
          </span>
        ))}
        {(!selectedLabels || selectedLabels.length === 0) && (
          <span className="text-sm text-gray-400">No labels</span>
        )}
      </div>

      {/* Add Label Button/Dropdown */}
      {editable && (
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-800"
            disabled={loading}
            data-testid="add-label-btn"
          >
            {loading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Tag size={14} />
            )}
            Add Label
            <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {isOpen && (
            <div className="absolute z-50 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg py-2 max-h-60 overflow-y-auto">
              {allLabels.length === 0 ? (
                <p className="px-4 py-2 text-sm text-gray-500">No labels available</p>
              ) : (
                allLabels.map(label => (
                  <button
                    key={label.id}
                    onClick={() => handleToggleLabel(label.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-gray-50 ${
                      selectedLabels.includes(label.id) ? 'bg-gray-50' : ''
                    }`}
                    data-testid={`label-option-${label.id}`}
                  >
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: label.color }}
                    />
                    <span className="flex-1">{label.name}</span>
                    {selectedLabels.includes(label.id) && (
                      <span className="text-teal-600 text-xs">✓</span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Label Filter Component for Broadcasts
 * Allows filtering recipients by labels
 */
export const LabelFilter = ({ selectedLabels = [], onLabelsChange }) => {
  const { token } = useAuth();
  const [allLabels, setAllLabels] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const loadLabels = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/labels`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAllLabels(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Failed to load labels');
      }
    };
    if (token) loadLabels();
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (labelId) => {
    const newLabels = selectedLabels.includes(labelId)
      ? selectedLabels.filter(id => id !== labelId)
      : [...selectedLabels, labelId];
    onLabelsChange(newLabels);
  };

  const getSelectedLabelObjects = () => {
    return allLabels.filter(label => selectedLabels.includes(label.id));
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="label-filter">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-colors ${
          selectedLabels.length > 0
            ? 'bg-teal-50 border-teal-200 text-teal-700'
            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
        }`}
        data-testid="label-filter-btn"
      >
        <Tag size={16} />
        Labels
        {selectedLabels.length > 0 && (
          <span className="px-1.5 py-0.5 bg-teal-600 text-white rounded text-xs">
            {selectedLabels.length}
          </span>
        )}
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg">
          <div className="p-3 border-b border-gray-100">
            <p className="text-xs text-gray-500 font-medium">Filter by Labels</p>
          </div>
          
          {/* Selected Labels */}
          {selectedLabels.length > 0 && (
            <div className="p-3 border-b border-gray-100 flex flex-wrap gap-2">
              {getSelectedLabelObjects().map(label => (
                <span
                  key={label.id}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
                  style={{ backgroundColor: label.color + '20', color: label.color }}
                >
                  {label.name}
                  <button onClick={() => handleToggle(label.id)}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Label Options */}
          <div className="py-2 max-h-60 overflow-y-auto">
            {allLabels.length === 0 ? (
              <p className="px-4 py-2 text-sm text-gray-500">No labels available</p>
            ) : (
              allLabels.map(label => (
                <button
                  key={label.id}
                  onClick={() => handleToggle(label.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-gray-50 ${
                    selectedLabels.includes(label.id) ? 'bg-gray-50' : ''
                  }`}
                  data-testid={`filter-label-${label.id}`}
                >
                  <div
                    className="w-4 h-4 rounded flex items-center justify-center border"
                    style={{ 
                      backgroundColor: selectedLabels.includes(label.id) ? label.color : 'white',
                      borderColor: label.color
                    }}
                  >
                    {selectedLabels.includes(label.id) && (
                      <span className="text-white text-xs">✓</span>
                    )}
                  </div>
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: label.color }}
                  />
                  <span className="flex-1">{label.name}</span>
                </button>
              ))
            )}
          </div>

          {selectedLabels.length > 0 && (
            <div className="p-3 border-t border-gray-100">
              <button
                onClick={() => onLabelsChange([])}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LabelSelector;
